
import { useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';

export default function Careers() {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: 'ri-heart-line',
      title: 'Health & Wellness',
      description: 'Comprehensive health insurance, mental health support, and wellness programs.'
    },
    {
      icon: 'ri-time-line',
      title: 'Flexible Schedule',
      description: 'Work-life balance with flexible hours and remote work options.'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Learning & Growth',
      description: 'Professional development budget and continuous learning opportunities.'
    },
    {
      icon: 'ri-team-line',
      title: 'Great Team',
      description: 'Work with passionate, talented people who care about making a difference.'
    },
    {
      icon: 'ri-rocket-line',
      title: 'Innovation',
      description: 'Be part of cutting-edge AI technology that helps people succeed.'
    },
    {
      icon: 'ri-gift-line',
      title: 'Perks & Benefits',
      description: 'Competitive salary, equity options, and amazing office perks.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div
              className="flex items-center cursor-pointer"
              onClick={() => navigate('/')}
            >
              <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                <i className="ri-robot-line text-xl text-white"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                PitchMe.AI
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={() => navigate('/login')}>
                Sign In
              </Button>
              <Button onClick={() => navigate('/login')}>
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 py-20">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-blue-600/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Join Our Team
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Help us build the future of interview preparation and empower millions
            of professionals to achieve their career goals.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Work With Us?
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                At PitchMe.AI, we're not just building a product - we're creating a movement
                that democratizes career success. Every day, we help thousands of people
                gain the confidence they need to land their dream jobs.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Join a team of passionate innovators who believe that everyone deserves
                a fair chance to showcase their potential, regardless of their background
                or interview experience.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20diverse%20team%20working%20together%20in%20bright%20collaborative%20office%20space%2C%20professionals%20brainstorming%20around%20computer%20screens%2C%20innovative%20startup%20atmosphere%2C%20natural%20lighting%2C%20creative%20workspace%20environment&width=600&height=400&seq=careers&orientation=landscape"
                alt="Our Team"
                className="w-full h-auto rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why You'll Love Working Here
            </h2>
            <p className="text-xl text-gray-600">
              We take care of our team so they can take care of our users
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} padding="lg" className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Current Openings Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Current Openings
          </h2>

          <Card padding="md" className="text-center">
            <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-search-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">
              No Open Positions Available
            </h3>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              We're not actively hiring at the moment, but we're always interested
              in connecting with talented individuals who share our mission.
            </p>
            <p className="text-gray-600 mb-8">
              If you're passionate about AI, career development, or helping people succeed,
              we'd love to hear from you. Send us your resume and we'll keep you in mind
              for future opportunities.
            </p>
            {/* <Button
              size="lg"
              onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
              className="px-8"
            >
              Get In Touch
              <i className="ri-mail-line ml-2"></i>
            </Button> */}
          </Card>
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            Our Culture
          </h2>
          <p className="text-xl text-purple-100 mb-8 leading-relaxed">
            We believe in transparency, continuous learning, and celebrating both
            individual achievements and team successes. Our culture is built on
            trust, innovation, and the shared goal of making interview preparation
            accessible to everyone.
          </p>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lightbulb-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Innovation First</h3>
              <p className="text-purple-100">Always pushing boundaries and exploring new possibilities</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-team-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Collaboration</h3>
              <p className="text-purple-100">Working together to achieve extraordinary results</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Impact Driven</h3>
              <p className="text-purple-100">Every decision is made with our users' success in mind</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center mr-2">
                  <i className="ri-robot-line text-white"></i>
                </div>
                <h3 className="text-xl font-bold">
                  PitchMe.AI
                </h3>
              </div>
              <p className="text-gray-400">
                Master your interviews with AI-powered practice and feedback.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/#features" className="hover:text-white">Features</a></li>
                <li><a href="/#pricing" className="hover:text-white">Pricing</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/about" className="hover:text-white">About</a></li>
                <li><a href="/careers" className="hover:text-white">Careers</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                {/* <li>
                  <button
                    onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
                    className="hover:text-white text-left"
                  >
                    Help Center
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
                    className="hover:text-white text-left"
                  >
                    Contact
                  </button>
                </li> */}
                <li><a href="/terms" className="hover:text-white">Terms</a></li>

                <li><a href="/privacy" className="hover:text-white">Privacy</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 flex justify-between items-center">
            <p className="text-gray-400">
              © 2024 PitchMe.AI. All rights reserved.
            </p>
            <a
              href="https://readdy.ai/?origin=logo"
              className="text-gray-400 hover:text-white text-sm"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}